import React, { createContext, useContext, useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const AppContext = createContext();

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize Supabase client
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
  const supabaseKey = process.env.REACT_APP_SUPABASE_KEY || '';
  const supabase = createClient(supabaseUrl, supabaseKey);

  // Fetch products and services from Supabase on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch products
        const { data: productsData, error: productsError } = await supabase
          .from('products')
          .select('*')
          .order('created_at', { ascending: false });

        if (productsError) throw productsError;

        // Fetch services
        const { data: servicesData, error: servicesError } = await supabase
          .from('services')
          .select('*')
          .order('created_at', { ascending: false });

        if (servicesError) throw servicesError;

        // Transform snake_case to camelCase for UI compatibility
        const productsForUI = (productsData || []).map(p => ({
          ...p,
          inStock: p.in_stock,
        }));

        const servicesForUI = (servicesData || []).map(s => ({
          ...s,
          maxStudents: s.max_students,
        }));

        setProducts(productsForUI);
        setServices(servicesForUI);
      } catch (err) {
        console.error('Error fetching data from Supabase:', err);
        setError(err.message);
        setProducts([]);
        setServices([]);
      } finally {
        setLoading(false);
      }
    };

    if (supabaseUrl && supabaseKey) {
      fetchData();
    } else {
      console.warn('Supabase credentials not configured');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add product to Supabase
  const addProduct = async (productData) => {
    try {
      const { data, error } = await supabase
        .from('products')
        .insert([
          {
            name: productData.name,
            description: productData.description,
            price: parseFloat(productData.price),
            category: productData.category,
            image: productData.image,
            in_stock: productData.inStock,
          },
        ])
        .select();

      if (error) throw error;

      if (data && data[0]) {
        const newProduct = {
          ...data[0],
          inStock: data[0].in_stock,
        };
        setProducts([newProduct, ...products]);
      }

      return data ? data[0] : null;
    } catch (err) {
      console.error('Error adding product:', err);
      throw err;
    }
  };

  // Update product in Supabase
  const updateProduct = async (id, productData) => {
    try {
      const { data, error } = await supabase
        .from('products')
        .update({
          name: productData.name,
          description: productData.description,
          price: parseFloat(productData.price),
          category: productData.category,
          image: productData.image,
          in_stock: productData.inStock,
        })
        .eq('id', id)
        .select();

      if (error) throw error;

      if (data && data[0]) {
        const updatedProduct = {
          ...data[0],
          inStock: data[0].in_stock,
        };
        setProducts(products.map(p => (p.id === id ? updatedProduct : p)));
      }

      return data ? data[0] : null;
    } catch (err) {
      console.error('Error updating product:', err);
      throw err;
    }
  };

  // Delete product from Supabase
  const deleteProduct = async (id) => {
    try {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setProducts(products.filter(p => p.id !== id));
      return true;
    } catch (err) {
      console.error('Error deleting product:', err);
      throw err;
    }
  };

  // Add service to Supabase
  const addService = async (serviceData) => {
    try {
      const { data, error } = await supabase
        .from('services')
        .insert([
          {
            title: serviceData.title,
            description: serviceData.description,
            price: parseFloat(serviceData.price),
            duration: serviceData.duration,
            level: serviceData.level,
            max_students: parseInt(serviceData.maxStudents) || 1,
            available: serviceData.available,
          },
        ])
        .select();

      if (error) throw error;

      if (data && data[0]) {
        const newService = {
          ...data[0],
          maxStudents: data[0].max_students,
        };
        setServices([newService, ...services]);
      }

      return data ? data[0] : null;
    } catch (err) {
      console.error('Error adding service:', err);
      throw err;
    }
  };

  // Update service in Supabase
  const updateService = async (id, serviceData) => {
    try {
      const { data, error } = await supabase
        .from('services')
        .update({
          title: serviceData.title,
          description: serviceData.description,
          price: parseFloat(serviceData.price),
          duration: serviceData.duration,
          level: serviceData.level,
          max_students: parseInt(serviceData.maxStudents) || 1,
          available: serviceData.available,
        })
        .eq('id', id)
        .select();

      if (error) throw error;

      if (data && data[0]) {
        const updatedService = {
          ...data[0],
          maxStudents: data[0].max_students,
        };
        setServices(services.map(s => (s.id === id ? updatedService : s)));
      }

      return data ? data[0] : null;
    } catch (err) {
      console.error('Error updating service:', err);
      throw err;
    }
  };

  // Delete service from Supabase
  const deleteService = async (id) => {
    try {
      const { error } = await supabase
        .from('services')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setServices(services.filter(s => s.id !== id));
      return true;
    } catch (err) {
      console.error('Error deleting service:', err);
      throw err;
    }
  };

  // Convenience setters for backward compatibility (direct setState)
  const setProductsState = (updater) => {
    setProducts(updater instanceof Function ? updater(products) : updater);
  };

  const setServicesState = (updater) => {
    setServices(updater instanceof Function ? updater(services) : updater);
  };

  const value = {
    products,
    setProducts: setProductsState,
    services,
    setServices: setServicesState,
    // Direct Supabase operations (for ProductManager/ServiceManager to use)
    addProduct,
    updateProduct,
    deleteProduct,
    addService,
    updateService,
    deleteService,
    loading,
    error,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
